import {
	DeleteAction,
	IDataListColumn,
	IconButton,
	PaginatedAutoDataList,
	SplitButton,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useOpen,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { eventService } from "../../../frontend/src/shared/api/eventService";
import { createEventUserService } from "../../api/userService";
import { PermissionName } from "../../types/permission";
import { EventUserResource } from "../../types/user";
import { BulkEditEventUserResponsibilityAction } from "./BulkEditEventUserResponsibilityAction";
import { EditEventUserResponsibilityDrawer } from "./EditEventUserResponsibilityDrawer";
import { EventUserAttachAction } from "./EventUserAttachAction";
import { EventUserGroupAttachAction } from "./EventUserGroupAttachAction";

interface Props {
	keysToInvalidate?: string[][];
	isEditableByUser: boolean;
}

export const EventUsersList: FC<Props> = ({ isEditableByUser, keysToInvalidate }) => {
	const { t } = useTranslation();
	const { eventId = "" } = useParams<"eventId">();
	const eventUserService = useMemo(() => createEventUserService(Number(eventId)), [eventId]);
	const queryClient = useQueryClient();
	const { open, handleOpen, handleClose } = useOpen();
	const [user, setUser] = useState<EventUserResource | null>(null);
	const { showToast } = useToasts();
	const {
		open: openUserGroupsDrawer,
		handleOpen: handleOpenUserGroupsDrawer,
		handleClose: handleCloseUserGroupsDrawer,
	} = useOpen();
	const { open: openUserDrawer, handleOpen: handleOpenUserDrawer, handleClose: handleCloseUserDrawer } = useOpen();
	const hasUpdatePermission = useHasPermission<PermissionName>(["event.update"]);

	const reloadList = useCallback(() => {
		void queryClient.invalidateQueries({ queryKey: eventUserService.listKey });
		void queryClient.invalidateQueries({ queryKey: eventService.listOverviewKey });
		keysToInvalidate && keysToInvalidate.forEach((key) => void queryClient.invalidateQueries({ queryKey: key }));
	}, [queryClient, eventUserService.listKey, keysToInvalidate]);

	const detachUsers = useCallback(
		async (userIds: string[]) => {
			const response = await eventUserService.detachUsers(userIds);
			if (isSuccess(response)) {
				reloadList();
				showToast({ type: "success", body: t("User was successfully detached."), title: t("Detach user") });
			}
		},
		[eventUserService, reloadList, showToast, t],
	);

	const openUserResponsibilityDrawer = useCallback(
		(user: EventUserResource) => {
			setUser(user);
			handleOpen();
		},
		[handleOpen],
	);

	const dataListColumns: IDataListColumn<EventUserResource>[] = useMemo(
		() => [
			{
				heading: t("Firstname"),
				valueKey: "firstName",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Lastname"),
				valueKey: "lastName",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Email"),
				valueKey: "email",
				type: "value",
				sortable: true,
			},
			{
				heading: t("Responsibilities"),
				valueKey: "userResponsibilities",
				type: "value",
				render: (value) => value.userResponsibilities.map(({ name }) => name).join(", "),
			},
			{
				size: "max-content",
				type: "actions",
				heading: t("Actions"),
				action: (user) =>
					(hasUpdatePermission || isEditableByUser) && (
						<>
							<IconButton
								title={t("Edit user responsibilities")}
								icon="edit"
								onClick={() => openUserResponsibilityDrawer(user)}
								size="small"
							/>
							<DeleteAction
								iconButton={true}
								action={() => detachUsers([user.id])}
								heading={t("Detach user")}
								labels={{ close: t("Close"), confirm: t("Confirm") }}
								title={t("Detach user")}
								size="small"
							>
								{t(`Are you sure you want to remove the user {{firstName}} {{lastName}} ({{email}}) from the event?`, {
									firstName: user.firstName,
									lastName: user.lastName,
									email: user.email,
								})}
							</DeleteAction>
						</>
					),
			},
		],
		[detachUsers, hasUpdatePermission, isEditableByUser, openUserResponsibilityDrawer, t],
	);

	return (
		<>
			<PaginatedAutoDataList
				variant="outlined"
				name="eventUsers"
				heading={t("Attached users")}
				empty={t("No attached users")}
				columns={dataListColumns}
				listFetchConfig={laravelPaginatedListFetchConfig(eventUserService.listKey, eventUserService.index)}
				enableBulkEdit={hasUpdatePermission || isEditableByUser}
				bulkEditActions={
					<BulkEditEventUserResponsibilityAction
						reloadList={reloadList}
						editUserResponsibility={eventUserService.editResponsibility}
					/>
				}
				initialSearch={{
					search: "",
					searchFields: [
						{ label: t("Firstname"), name: "firstname" },
						{ label: t("Lastname"), name: "lastname" },
						{ label: t("E-Mail"), name: "email" },
					],
				}}
				headerActions={
					(hasUpdatePermission || isEditableByUser) && (
						<>
							<SplitButton
								options={[
									{ title: t("Attach users"), onClick: handleOpenUserDrawer },
									{ title: t("Attach user groups"), onClick: handleOpenUserGroupsDrawer },
								]}
								color="primary"
								size="default"
							/>
							<EventUserAttachAction
								eventId={eventId}
								reloadList={reloadList}
								attachUsers={eventUserService.attachUsers}
								open={openUserDrawer}
								handleClose={handleCloseUserDrawer}
							/>
							<EventUserGroupAttachAction
								eventId={eventId}
								reloadList={reloadList}
								attachUserGroups={eventUserService.attachUserGroups}
								open={openUserGroupsDrawer}
								handleClose={handleCloseUserGroupsDrawer}
							/>
						</>
					)
				}
			/>
			{user && (
				<EditEventUserResponsibilityDrawer
					open={open}
					handleClose={handleClose}
					onClosed={() => setUser(null)}
					selectedRows={[user.id]}
					reloadList={reloadList}
					editUserResponsibility={eventUserService.editResponsibility}
					initialValues={{ responsibilities: user.userResponsibilities }}
					eventId={eventId}
				/>
			)}
		</>
	);
};
